import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { ConnectionQuality, ConnectionState, RoomEvent, Track, VideoQuality } from 'livekit-client';
import {
  LiveKitRoom,
  RoomAudioRenderer,
  useRemoteParticipants,
  useRoomContext,
  useTracks,
} from '@livekit/components-react';
import EgressHelper from './egressHelper';
import VideoGrid from '../room/VideoGrid';
import ScreenShareView from '../room/ScreenShareView';
import { isRenderableScreenShareTrack } from '../screenShareUtils';
import '../room/room.css';
import './recordingTemplate.css';

const FRAME_DECODE_MS = 5000;
/** If readiness heuristics never fire while **Connected**, emit START_RECORDING (reduces egress 412). */
const START_RECORDING_FALLBACK_MS = 25_000;
/**
 * If the room stays slow (e.g. long **Connecting**), still emit before typical user stop / ~2m cloud wait.
 * Any state except Disconnected — headless egress still attached to the composite pipeline.
 */
const START_RECORDING_FALLBACK_LOOSE_MS = 45_000;

function connectionQualityToLevel(quality) {
  if (quality == null) return 0;
  const q = typeof quality === 'string' ? quality.toLowerCase() : String(quality);
  switch (q) {
    case 'excellent':
    case ConnectionQuality.Excellent:
      return 4;
    case 'good':
    case ConnectionQuality.Good:
      return 3;
    case 'poor':
    case ConnectionQuality.Poor:
      return 2;
    case 'lost':
    case ConnectionQuality.Lost:
      return 1;
    default:
      return 0;
  }
}

function RecordingComposite({ initialLayout }) {
  const room = useRoomContext();
  const remotes = useRemoteParticipants();
  const [layout, setLayout] = useState(initialLayout || 'grid');
  const [activeSpeakerSid, setActiveSpeakerSid] = useState(null);
  const [networkQuality, setNetworkQuality] = useState({});

  // @livekit/components-core defaults onlySubscribed to TRUE — refs are omitted until publication.track
  // exists. Egress/headless often needs refs earlier so we subscribe + render the <video> pipeline.
  const tracks = useTracks(
    [Track.Source.Camera, Track.Source.ScreenShare, Track.Source.Microphone],
    { onlySubscribed: false }
  );

  const screenTrack = useMemo(() => {
    const screenRefs = tracks.filter((t) => t.source === Track.Source.ScreenShare);
    if (!screenRefs.length) return undefined;
    const live = screenRefs.find((t) => isRenderableScreenShareTrack(t));
    if (live) return live;
    return screenRefs[0];
  }, [tracks]);

  const screenSharingSids = useMemo(() => {
    const s = new Set();
    for (const t of tracks) {
      if (
        t.source === Track.Source.ScreenShare &&
        t.participant?.sid &&
        isRenderableScreenShareTrack(t)
      ) {
        s.add(t.participant.sid);
      }
    }
    return s;
  }, [tracks]);

  const remoteParticipantsWithTracks = useMemo(() => {
    return remotes.map((participant) => {
      const cameraTrack =
        tracks.find(
          (t) => t.participant.sid === participant.sid && t.source === Track.Source.Camera
        ) || null;
      return { participant, cameraTrack };
    });
  }, [remotes, tracks]);

  const stageTrack = screenTrack || null;
  /** Recorder has no camera tile of its own — keep every remote (including sharer) in the strip. */
  const stageCameraSid =
    stageTrack?.source === Track.Source.Camera ? stageTrack.participant.sid : null;
  const sidebarParticipantsWithTracks = remoteParticipantsWithTracks.filter(
    ({ participant }) => participant.sid !== stageCameraSid
  );

  useEffect(() => {
    EgressHelper.onLayoutChanged((next) => {
      if (typeof next === 'string') setLayout(next || 'grid');
    });
  }, []);

  useLayoutEffect(() => {
    EgressHelper.setRoom(room);
  }, [room]);

  /**
   * Recorder must subscribe to remote video (camera + screen). Also re-enable tracks and request
   * HIGH quality for screen — adaptiveStream/off-screen logic can otherwise starve headless egress.
   */
  useEffect(() => {
    if (!room) return;
    const subscribeRemoteVideo = (participant) => {
      if (!participant?.isLocal) {
        participant.tracks.forEach((pub) => {
          if (pub.kind !== Track.Kind.Video) return;
          if (!pub.isSubscribed) pub.setSubscribed(true);
          if (typeof pub.setEnabled === 'function' && !pub.isEnabled) {
            pub.setEnabled(true);
          }
          if (pub.source === Track.Source.ScreenShare && typeof pub.setVideoQuality === 'function') {
            try {
              pub.setVideoQuality(VideoQuality.HIGH);
            } catch {
              /* ignore */
            }
          }
        });
      }
    };
    const syncAllRemotes = () => {
      room.participants?.forEach((p) => subscribeRemoteVideo(p));
    };
    const onTrackPublished = (_publication, participant) => subscribeRemoteVideo(participant);
    syncAllRemotes();
    room.on(RoomEvent.Connected, syncAllRemotes);
    room.on(RoomEvent.Reconnected, syncAllRemotes);
    room.on(RoomEvent.ParticipantConnected, subscribeRemoteVideo);
    room.on(RoomEvent.TrackPublished, onTrackPublished);
    return () => {
      room.off(RoomEvent.Connected, syncAllRemotes);
      room.off(RoomEvent.Reconnected, syncAllRemotes);
      room.off(RoomEvent.ParticipantConnected, subscribeRemoteVideo);
      room.off(RoomEvent.TrackPublished, onTrackPublished);
    };
  }, [room]);

  /**
   * Egress watches the page console for START_RECORDING. If the bot disconnects quickly (bad token,
   * duplicate identity, room ended, etc.), long fallbacks never run and egress returns 412. Emit as
   * soon as WebRTC is connected so the start signal usually wins the race (startRecording is idempotent).
   */
  useEffect(() => {
    if (!room) return;
    const signalReady = () => {
      setTimeout(() => EgressHelper.startRecording(), 0);
    };
    if (room.state === ConnectionState.Connected) {
      signalReady();
    }
    const onConnected = () => signalReady();
    room.on(RoomEvent.Connected, onConnected);
    return () => {
      room.off(RoomEvent.Connected, onConnected);
    };
  }, [room]);

  useEffect(() => {
    const onSpeakers = (speakers) => {
      setActiveSpeakerSid(speakers.length > 0 ? speakers[0].sid : null);
    };
    room.on(RoomEvent.ActiveSpeakersChanged, onSpeakers);
    return () => {
      room.off(RoomEvent.ActiveSpeakersChanged, onSpeakers);
    };
  }, [room]);

  useEffect(() => {
    const updateQuality = (participant) => {
      const level = connectionQualityToLevel(participant.connectionQuality);
      setNetworkQuality((prev) => ({ ...prev, [participant.sid]: level }));
    };

    room.participants?.forEach((p) => updateQuality(p));

    const onCQ = (quality, participant) => {
      const level = connectionQualityToLevel(quality ?? participant.connectionQuality);
      setNetworkQuality((prev) => ({ ...prev, [participant.sid]: level }));
    };
    room.on(RoomEvent.ConnectionQualityChanged, onCQ);
    room.on(RoomEvent.ParticipantConnected, updateQuality);
    const onParticipantDisconnected = (p) => {
      setNetworkQuality((prev) => {
        const copy = { ...prev };
        delete copy[p.sid];
        return copy;
      });
    };
    room.on(RoomEvent.ParticipantDisconnected, onParticipantDisconnected);

    return () => {
      room.off(RoomEvent.ConnectionQualityChanged, onCQ);
      room.off(RoomEvent.ParticipantConnected, updateQuality);
      room.off(RoomEvent.ParticipantDisconnected, onParticipantDisconnected);
    };
  }, [room]);

  useEffect(() => {
    const startTime = Date.now();
    const interval = setInterval(async () => {
      let shouldStart = false;
      let hasVideo = false;
      let hasSubscribed = false;
      let hasDecoded = false;

      const remotes = room.participants;
      if (!remotes) return;
      for (const p of Array.from(remotes.values())) {
        for (const pub of Array.from(p.tracks.values())) {
          if (pub.isSubscribed) hasSubscribed = true;
          if (pub.kind === Track.Kind.Video && pub.videoTrack) {
            hasVideo = true;
            try {
              const stats = await pub.videoTrack.getRTCStatsReport();
              if (stats) {
                let decodedHere = false;
                stats.forEach((item) => {
                  if (item.type === 'inbound-rtp' && item.framesDecoded > 0) {
                    decodedHere = true;
                  }
                });
                if (decodedHere) hasDecoded = true;
              }
            } catch {
              /* ignore */
            }
          }
        }
      }

      const dt = Date.now() - startTime;
      if (hasDecoded) shouldStart = true;
      else if (!hasVideo && hasSubscribed && dt > 500) shouldStart = true;
      else if (dt > FRAME_DECODE_MS && hasSubscribed) shouldStart = true;
      else if (
        dt > START_RECORDING_FALLBACK_MS &&
        room.state === ConnectionState.Connected
      ) {
        // Avoid egress 412 when subscriptions/decoded-frame heuristics never flip (headless quirks or
        // very delayed track subscription). LiveKit still muxes whatever is available.
        shouldStart = true;
        console.warn(
          '[recording-template] START_RECORDING fallback: connected',
          START_RECORDING_FALLBACK_MS / 1000,
          's without normal readiness gate — recording may be black/silent until tracks arrive.'
        );
      } else if (
        dt > START_RECORDING_FALLBACK_LOOSE_MS &&
        room.state !== ConnectionState.Disconnected
      ) {
        shouldStart = true;
        console.warn(
          '[recording-template] START_RECORDING fallback: room state',
          room.state,
          `after ${START_RECORDING_FALLBACK_LOOSE_MS / 1000}s (not disconnected) — may still be connecting; recording may start before tracks settle.`
        );
      }

      if (shouldStart) {
        EgressHelper.startRecording();
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [room, room.state]);

  let effectiveLayout = layout || 'grid';
  if (screenTrack && effectiveLayout.startsWith('grid')) {
    effectiveLayout = effectiveLayout.replace(/^grid/, 'speaker');
  }

  const light = effectiveLayout.endsWith('-light') || layout.endsWith('-light');

  return (
    <div
      className={`room-container recording-template ${light ? 'recording-template--light' : ''}`}
    >
      <RoomAudioRenderer />
      {stageTrack && effectiveLayout.startsWith('speaker') ? (
        <ScreenShareView
          key={stageTrack.publication?.trackSid || stageTrack.participant?.sid || 'stage'}
          screenTrack={stageTrack}
          participantsWithTracks={sidebarParticipantsWithTracks}
          onTileClick={() => {}}
          networkQuality={networkQuality}
          activeSpeakerSid={activeSpeakerSid}
          screenSharingSids={screenSharingSids}
        />
      ) : (
        <VideoGrid
          participantsWithTracks={remoteParticipantsWithTracks}
          onTileClick={() => {}}
          networkQuality={networkQuality}
          activeSpeakerSid={activeSpeakerSid}
          screenSharingSids={screenSharingSids}
        />
      )}
    </div>
  );
}

/**
 * Headless-safe LiveKit room for Room Composite egress (query: url, token, layout).
 * Renders grid / speaker+screen using the same TrackTile as the meeting UI.
 */
export default function RecordingTemplateRoom({ serverUrl, token, layout }) {
  return (
    <LiveKitRoom
      serverUrl={serverUrl}
      token={token}
      connect
      audio={false}
      video={false}
      options={{
        // Headless recorder: avoid SFU dropping simulcast layers / sizing screen share as "inactive"
        adaptiveStream: false,
        dynacast: false,
      }}
      style={{ height: '100vh' }}
    >
      <RecordingComposite initialLayout={layout} />
    </LiveKitRoom>
  );
}
